import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { isAdmin } from '../utils/permissions';
import { formatDate } from '../utils/format';
import { canBuyInApp } from '../utils/native';
import {
  LICENSE_INACTIVE_PATH,
  clearStoredLicenseBlocked,
  storedUserLicenseBlocked,
} from '../utils/licenseRouting';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Cache the license check across renders so every page navigation
// doesn't refetch /me. The cache invalidates after 60s OR when the
// route changes to one of the always-allowed paths (/checkout etc).
let _meCache = null;
let _meCacheAt = 0;
const ME_TTL_MS = 60_000;

// Subscribers notified when the cache is force-invalidated so the live
// component can refetch immediately instead of waiting for the next
// route change. Used by CheckoutPage, BillingSettingsPage etc.
const _subscribers = new Set();

/**
 * Drop the cached /api/billing/me payload. Call this from any page
 * that just performed a license-mutating action (checkout success,
 * forget-card, cancel-renewal) so the guard re-evaluates immediately.
 */
export const invalidateLicenseCache = () => {
  _meCache = null;
  _meCacheAt = 0;
  _subscribers.forEach((fn) => {
    try { fn(); } catch { /* swallow — never let one subscriber block others */ }
  });
};

const ALWAYS_ALLOW = [
  '/checkout',
  '/modules/admin/billing',
  '/login',
  '/logout',
  '/profile',  // user must be able to edit own profile / change password
];

const STATUS_BLOCKING = {
  trial_expired: {
    icon: '⏰',
    title: 'shell.licenseGuard.trialExpired.title',
    body: 'shell.licenseGuard.trialExpired.body',
    cta: 'shell.licenseGuard.buyLicense',
    accent: 'from-amber-500 to-orange-600',
  },
  past_due: {
    icon: '⚠️',
    title: 'shell.licenseGuard.pastDue.title',
    body: 'shell.licenseGuard.pastDue.body',
    cta: 'shell.licenseGuard.manageLicense',
    accent: 'from-amber-600 to-red-600',
  },
  suspended: {
    icon: '🔒',
    title: 'shell.licenseGuard.suspended.title',
    body: 'shell.licenseGuard.suspended.body',
    cta: 'shell.licenseGuard.buyLicense',
    accent: 'from-red-600 to-red-700',
  },
  cancelled: {
    icon: '🚪',
    title: 'shell.licenseGuard.cancelled.title',
    body: 'shell.licenseGuard.cancelled.body',
    cta: 'shell.licenseGuard.buyLicense',
    accent: 'from-gray-600 to-gray-700',
  },
};

const TrialBanner = ({ daysLeft }) => {
  const { t } = useTranslation();
  // Tone tiers: > 7 days = informational, 3–7 = nudging, ≤ 3 = urgent.
  const tier = daysLeft <= 3 ? 'red' : daysLeft <= 7 ? 'amber' : 'blue';
  const palette = {
    blue:  { bg: 'bg-blue-50',  border: 'border-blue-200',  text: 'text-blue-800',  link: 'hover:text-blue-900',  icon: '🎁' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-900', link: 'hover:text-amber-700', icon: '⏰' },
    red:   { bg: 'bg-red-50',   border: 'border-red-300',   text: 'text-red-800',   link: 'hover:text-red-900',   icon: '🚨' },
  }[tier];
  const cta = tier === 'blue' ? t('shell.licenseGuard.getLicenseNow') : t('shell.licenseGuard.upgradeNow');
  return (
    <div className={`${palette.bg} border-b ${palette.border} px-4 py-2 text-sm ${palette.text} text-center`}>
      {palette.icon} <Trans i18nKey="shell.licenseGuard.trialEndsIn" count={daysLeft} components={{ b: <b /> }} />{' '}
      {canBuyInApp() && (
        <Link to="/checkout" className={`font-semibold underline ${palette.link}`}>
          {cta}
        </Link>
      )}
    </div>
  );
};

const BlockingModal = ({ status, license_expires_at }) => {
  const { t } = useTranslation();
  const cfg = STATUS_BLOCKING[status] || STATUS_BLOCKING.suspended;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className={`bg-gradient-to-br ${cfg.accent} text-white rounded-2xl shadow-2xl max-w-md w-full p-8`}>
        <h1 className="text-2xl font-bold mb-3">{cfg.icon} {t(cfg.title)}</h1>
        <p className="text-white/90 mb-6 leading-relaxed">{t(cfg.body)}</p>
        {license_expires_at && (
          <p className="text-xs text-white/70 mb-6">
            {t('shell.licenseGuard.expiresAt', { date: formatDate(license_expires_at) })}
          </p>
        )}
        <div className="flex flex-col gap-2">
          {canBuyInApp() && (
            <Link to="/checkout"
              className="bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold text-center hover:bg-gray-100 transition">
              💳 {t(cfg.cta)}
            </Link>
          )}
          <Link to="/modules/admin/billing"
            className="text-white/80 text-sm text-center hover:text-white">
            {t('shell.licenseGuard.viewLicenseInfo')}
          </Link>
        </div>
      </div>
    </div>
  );
};

/**
 * Wraps protected routes. Reads /api/billing/me once per minute and:
 * - Renders a TrialBanner above children when trial has < 5 days left
 * - Renders a BlockingModal (no children) when license_status is one of
 *   trial_expired / past_due / suspended / cancelled, EXCEPT when the
 *   user is already on /checkout or /modules/admin/billing (those must
 *   stay reachable so the user can pay).
 */
const LicenseGuard = ({ children }) => {
  const location = useLocation();
  const [me, setMe] = useState(_meCache);
  const [refetchTick, setRefetchTick] = useState(0);

  // Subscribe to invalidate calls so a checkout success / card change
  // forces an immediate refetch even if the URL didn't change.
  useEffect(() => {
    const onInvalidate = () => {
      setMe(null);
      setRefetchTick((t) => t + 1);
    };
    _subscribers.add(onInvalidate);
    return () => { _subscribers.delete(onInvalidate); };
  }, []);

  useEffect(() => {
    // Superuser is platform-level; no tenant license to read. Skip the
    // /me call entirely so the console doesn't fill with 400s and the
    // BlockingModal logic short-circuits to 'never block'.
    let role = null;
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      role = u?.role;
    } catch { /* ignore parse error */ }
    if (role === 'superuser' || role === 'supplier_user') {
      setMe({ license_status: 'unknown' });
      return;
    }

    const fresh = Date.now() - _meCacheAt < ME_TTL_MS;
    if (_meCache && fresh) {
      setMe(_meCache);
      return;
    }
    let cancelled = false;
    axios.get(`${API}/api/billing/me`).then(({ data }) => {
      if (cancelled) return;
      _meCache = data;
      _meCacheAt = Date.now();
      if (!STATUS_BLOCKING[data?.license_status]) clearStoredLicenseBlocked();
      setMe(data);
    }).catch(() => {
      // /me failed — assume best (don't block UI on a flaky network)
      if (!cancelled) setMe({ license_status: 'unknown' });
    });
    return () => { cancelled = true; };
  }, [location.pathname, refetchTick]);

  // While fetching, render children optimistically (avoid flash of blocked
  // UI) -- unless login already told us the licence is lapsed: then the page
  // would only fire module requests that 403 (one error toast each).
  if (!me) return storedUserLicenseBlocked() ? null : children;

  const status = me.license_status;
  const onAllowed = ALWAYS_ALLOW.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'));

  // Blocking states — render modal unless we're on an allowed page
  if (STATUS_BLOCKING[status] && !onAllowed) {
    // Only a company admin can pay; others get the "contact your admin" page
    // instead of a checkout they cannot complete.
    let storedUser = null;
    try { storedUser = JSON.parse(localStorage.getItem('user') || 'null'); } catch { /* ignore */ }
    if (!isAdmin(storedUser)) return <Navigate to={LICENSE_INACTIVE_PATH} replace />;
    return <BlockingModal status={status} license_expires_at={me.license_expires_at} />;
  }

  // Trial countdown — banner is always visible during a trial so the
  // buyer can jump to /checkout from any page. Tone tiers based on
  // days remaining; suppressed only on /checkout itself (banner there
  // would be noise next to the inline countdown card) and on /signup
  // /verify-email which are pre-auth.
  let banner = null;
  if (status === 'trial' && me.trial_ends_at) {
    const suppressOn = ['/checkout', '/signup', '/verify-email', '/login'];
    const onSuppressed = suppressOn.some((p) =>
      location.pathname === p || location.pathname.startsWith(p + '/'),
    );
    if (!onSuppressed) {
      const daysLeft = Math.ceil(
        (new Date(me.trial_ends_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
      );
      if (daysLeft >= 0) {
        banner = <TrialBanner daysLeft={daysLeft} />;
      }
    }
  }

  return (
    <>
      {banner}
      {children}
    </>
  );
};

export default LicenseGuard;
